import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaExclamationTriangle, FaShieldAlt, FaBalanceScale } from 'react-icons/fa';

const RiskDisclaimerPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <main className="flex-grow container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg shadow-xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <FaExclamationTriangle className="text-5xl" />
            <div>
              <h1 className="text-4xl font-bold">Risk Disclaimer</h1>
              <p className="text-red-100 mt-2">Important Information You Must Read</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-8">
          
          {/* General Disclaimer */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <FaShieldAlt className="text-3xl text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">General Disclaimer</h2>
            </div>
            <div className="prose prose-sm max-w-none text-gray-700 space-y-3">
              <p>
                TrendVector is a <strong>data analysis and screening platform</strong> designed to help investors and traders 
                analyze market data, identify potential opportunities, and make more informed decisions. TrendVector is 
                <strong> NOT a registered investment advisor, broker-dealer, or financial planner</strong>.
              </p>
              <p>
                The information, analysis, and suggestions provided by TrendVector are for <strong>informational and educational 
                purposes only</strong> and should not be considered as financial, investment, or trading advice. You should 
                consult with a qualified financial advisor before making any investment decisions.
              </p>
            </div>
          </section>

          {/* No Financial Advice */}
          <section className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <FaBalanceScale className="text-yellow-600" />
              Not Financial Advice
            </h3>
            <div className="text-gray-800 space-y-2">
              <p>
                <strong>TrendVector does not provide personalized financial advice.</strong> All content, including but not 
                limited to stock screens, technical analysis, model recommendations, and trading signals, is provided for 
                general informational purposes only.
              </p>
              <p>
                Any decisions you make based on information from TrendVector are made <strong>entirely at your own risk</strong>. 
                TrendVector and its owners, employees, and affiliates are not responsible for any losses, damages, or negative 
                outcomes resulting from your use of the platform.
              </p>
            </div>
          </section>

          {/* Investment Risks */}
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Investment and Trading Risks</h3>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <ul className="space-y-3 text-gray-800">
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold mt-1">•</span>
                  <span><strong>Market Risk:</strong> Stock prices can and do fluctuate significantly. The value of your investments 
                  can go down as well as up, and you may lose some or all of your invested capital.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold mt-1">•</span>
                  <span><strong>F&O Trading Risk:</strong> Futures and Options trading is highly risky and can result in substantial 
                  losses exceeding your initial investment. Options can expire worthless, and leverage amplifies both gains and losses.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold mt-1">•</span>
                  <span><strong>Technical Analysis Risk:</strong> Technical indicators and patterns are not foolproof. They can 
                  generate false signals and do not guarantee future price movements.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold mt-1">•</span>
                  <span><strong>Model Risk:</strong> TrendVector's models are based on historical data and technical analysis. 
                  Past performance is not indicative of future results. Models may fail to predict market behavior.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold mt-1">•</span>
                  <span><strong>Execution Risk:</strong> When placing orders through connected brokers, there are risks including 
                  order rejection, partial fills, slippage, and technical failures.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold mt-1">•</span>
                  <span><strong>Liquidity Risk:</strong> Some stocks may have low trading volume, making it difficult to buy or 
                  sell at desired prices.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* No Guarantees */}
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">No Guarantees or Warranties</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                TrendVector makes <strong>no guarantees, representations, or warranties</strong> regarding the accuracy, 
                completeness, or reliability of the information, data, analysis, or recommendations provided on the platform.
              </p>
              <p>
                <strong>Past performance is not indicative of future results.</strong> Historical returns, backtested results, 
                and model performance should not be relied upon as an indication of future performance.
              </p>
              <p>
                While we strive to provide accurate and timely data, there may be delays, errors, or omissions in the 
                information provided. Market data, stock prices, and technical indicators are provided on an "as is" basis.
              </p>
            </div>
          </section>

          {/* Your Responsibility */}
          <section className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Your Responsibilities</h3>
            <div className="text-gray-800 space-y-3">
              <p>
                <strong>You are solely responsible for:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Conducting your own due diligence and research</li>
                <li>Evaluating the suitability of any investment for your specific situation</li>
                <li>Understanding the risks involved in each investment or trade</li>
                <li>Making your own independent investment decisions</li>
                <li>Monitoring your positions and managing risk appropriately</li>
                <li>Complying with all applicable laws and regulations</li>
                <li>Paying all applicable taxes on your trading activities</li>
              </ul>
              <p className="mt-4">
                <strong>Never invest more than you can afford to lose.</strong> Only risk capital that you can afford to 
                lose entirely should be used for trading or investing.
              </p>
            </div>
          </section>

          {/* Data Accuracy */}
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Data and Technical Limitations</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                TrendVector aggregates data from various sources including market data providers and connected broker APIs. 
                While we make reasonable efforts to ensure data accuracy:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Data may be delayed, incorrect, or incomplete</li>
                <li>Technical indicators are calculated based on available data and may have limitations</li>
                <li>Internet connectivity issues may affect data delivery or order placement</li>
                <li>Third-party APIs (broker connections) may experience outages or errors</li>
                <li>Model results depend on the quality and availability of historical data</li>
              </ul>
              <p className="mt-4">
                <strong>Always verify critical information</strong> through your broker's platform before making trading decisions.
              </p>
            </div>
          </section>

          {/* Broker Integration */}
          <section className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Broker Integration Disclaimer</h3>
            <div className="text-gray-800 space-y-3">
              <p>
                TrendVector offers integration with third-party brokers (such as Zerodha) to facilitate order placement. 
                However:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>TrendVector is not responsible for broker outages, order failures, or execution errors</li>
                <li>You are responsible for maintaining valid broker credentials and access tokens</li>
                <li>Orders placed through TrendVector are subject to your broker's terms and conditions</li>
                <li>TrendVector does not hold, control, or have access to your funds or securities</li>
                <li>Brokerage fees, taxes, and other charges are your responsibility</li>
              </ul>
              <p className="mt-4">
                <strong>You must read and agree to your broker's terms of service</strong> before using the broker integration features.
              </p>
            </div>
          </section>

          {/* F&O Specific Warning */}
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Futures & Options (F&O) Trading</h3>
            <div className="bg-red-100 border-2 border-red-400 rounded-lg p-6">
              <p className="text-red-900 font-semibold mb-3">
                ⚠️ F&O trading is highly speculative and carries extreme risk:
              </p>
              <ul className="space-y-2 text-red-900">
                <li className="flex items-start gap-2">
                  <span className="font-bold mt-1">•</span>
                  <span>Leverage can magnify both profits and losses exponentially</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold mt-1">•</span>
                  <span>Options can expire worthless, resulting in 100% loss of premium paid</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold mt-1">•</span>
                  <span>Futures contracts have unlimited loss potential</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold mt-1">•</span>
                  <span>F&O positions require adequate margin maintenance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold mt-1">•</span>
                  <span>Market gaps can cause losses beyond your stop-loss levels</span>
                </li>
              </ul>
              <p className="mt-4 font-bold text-red-900">
                F&O trading is suitable only for experienced traders who fully understand the risks and can afford significant losses.
              </p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Limitation of Liability</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                To the maximum extent permitted by law, TrendVector and its owners, employees, contractors, and affiliates 
                shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising 
                from or related to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Your use or inability to use the platform</li>
                <li>Any errors, omissions, or inaccuracies in the data or analysis provided</li>
                <li>Any investment or trading decisions made based on platform information</li>
                <li>Losses resulting from technical failures, outages, or connectivity issues</li>
                <li>Unauthorized access to your account or data breaches</li>
                <li>Any other matter relating to the platform or services</li>
              </ul>
            </div>
          </section>

          {/* Regulatory Compliance */}
          <section className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Regulatory Information</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                TrendVector is a technology platform providing data analysis tools. We are not regulated by SEBI (Securities 
                and Exchange Board of India) or any other financial regulatory authority. We do not:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide investment advice or recommendations</li>
                <li>Manage client funds or portfolios</li>
                <li>Act as a broker or intermediary</li>
                <li>Guarantee any returns or outcomes</li>
                <li>Endorse or recommend any specific stocks or trades</li>
              </ul>
              <p className="mt-4">
                If you need regulated financial advice, please consult a SEBI-registered investment advisor.
              </p>
            </div>
          </section>

          {/* User Acknowledgment */}
          <section className="bg-gradient-to-r from-indigo-50 to-blue-50 border-2 border-indigo-300 rounded-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">User Acknowledgment</h3>
            <div className="text-gray-800 space-y-3">
              <p className="font-semibold">By using TrendVector, you acknowledge and agree that:</p>
              <ul className="list-decimal list-inside space-y-2 ml-4">
                <li>You have read and understood this risk disclaimer in full</li>
                <li>You understand that all investment and trading decisions are your own</li>
                <li>You accept full responsibility for any profits or losses resulting from your decisions</li>
                <li>You will not hold TrendVector liable for any trading losses or adverse outcomes</li>
                <li>You understand the risks of stock market investing and F&O trading</li>
                <li>You have the financial capacity to bear potential losses</li>
                <li>You will conduct your own research and due diligence</li>
                <li>You will comply with all applicable laws and regulations</li>
              </ul>
            </div>
          </section>

          {/* Educational Purpose */}
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Educational Purpose</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                TrendVector's models, screens, and analysis tools are designed to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Help you learn about technical analysis and market screening</li>
                <li>Provide data visualization and pattern recognition tools</li>
                <li>Assist in identifying potential trading opportunities</li>
                <li>Streamline your research and analysis workflow</li>
              </ul>
              <p className="mt-4">
                <strong>These tools are not substitutes for professional financial advice.</strong> They should be used as 
                part of a comprehensive research process, not as the sole basis for investment decisions.
              </p>
            </div>
          </section>

          {/* Contact and Questions */}
          <section className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Questions or Concerns?</h3>
            <p className="text-gray-700 mb-4">
              If you have any questions about this risk disclaimer or TrendVector's services, please contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p>📧 Email: <a href="mailto:support@trendvector.in" className="text-blue-600 hover:underline">support@trendvector.in</a></p>
              <p>📄 <Link to="/terms" className="text-blue-600 hover:underline">Terms & Conditions</Link></p>
              <p>🔒 <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link></p>
            </div>
          </section>

          {/* Final Warning */}
          <section className="bg-red-50 border-2 border-red-500 rounded-lg p-6">
            <h3 className="text-2xl font-bold text-red-900 mb-4 flex items-center gap-2">
              <FaExclamationTriangle />
              Final Important Notice
            </h3>
            <div className="text-red-900 space-y-3">
              <p className="font-bold text-lg">
                NEVER INVEST MORE THAN YOU CAN AFFORD TO LOSE
              </p>
              <p>
                Trading and investing in the stock market involves substantial risk of loss. You should carefully consider 
                whether such trading is suitable for you in light of your financial condition, risk tolerance, and investment 
                objectives.
              </p>
              <p className="font-semibold">
                If you do not fully understand the risks involved or are not prepared to accept potential losses, please do 
                not use TrendVector for trading or investment purposes.
              </p>
            </div>
          </section>

          {/* Last Updated */}
          <div className="text-center text-sm text-gray-500 pt-6 border-t">
            <p>Last Updated: November 11, 2025</p>
            <p className="mt-2">This disclaimer is subject to change. Please review periodically.</p>
          </div>

        </div>

        {/* Back to Home Button */}
        <div className="text-center mt-8">
          <Link
            to="/"
            className="inline-block px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-semibold shadow-lg transition-all"
          >
            ← Back to Home
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RiskDisclaimerPage;

